'use client';

/**
 * CheckoutModal — 3-variant order checkout flow (physical / service / digital).
 *
 * Spec ref: SPEC-081 Fase 5 §6 Task C (3 checkout variants), §4.1 (service
 *           flow), §4.2 (digital flow); SPEC-081 Fase 4 §6.1 (checkout
 *           modal — physical baseline), §4.1 (createOrder flow), §5.1 (POST
 *           /api/market/orders); SPEC-NEGOCIO §10.5 (INV-MKT-09 escrow
 *           off-chain), §10.6 (INV-MKT-21 fulfillment method selection),
 *           §10.7 (servicios 50/50 + cancelación 30/20), §10.8 (digitales
 *           INV-MKT-14); ADR-039 + enmienda INV-MKT-21 (aprobada por Founder
 *           2026-08-28); SPEC-081 Fase 5 §0 Q4 (date picker single day) +
 *           Q5 (free integer guestCount).
 *
 * Variant dispatch (SPEC §6 Task C — UI shape verbatim):
 *   - `product.type === 'physical'` → 3-step: details (quantity +
 *     fulfillment method ship/pickup + address/pickup confirm) → summary →
 *     2FA. Calls `api.market.orders.create({ productType: 'physical', ... })`.
 *   - `product.type === 'service'`  → 3-step: reservation details (date
 *     picker + time range + guestCount + deposit/balance breakdown) →
 *     summary → 2FA. Calls `api.market.orders.create({ productType:
 *     'service', productId, guestCount, reservationDate, reservationTimeRange })`.
 *   - `product.type === 'digital'`  → 1-step (summary + 2FA + create).
 *     Shows product name + "Descarga inmediata tras la compra" + "Pago
 *     único: X créditos". Calls `api.market.orders.create({ productType:
 *     'digital', productId })`. On success: redirect to
 *     `/market/ordenes/{orderNumber}` where the download button is shown
 *     (Task C2 — `app/market/ordenes/[orderNumber]/page.tsx`).
 *
 * §6 Anti-Invención:
 *   - The 30/2/68 payout split + 24h auto-close + 72h seller timeout are
 *     NOT referenced here — those are backend-only concerns
 *     (`escrow.service.releaseEscrow` + cron jobs). The modal just creates
 *     the order; the lifecycle happens server-side.
 *   - The estimated total shown in Step 2 is a UX affordance — the
 *     backend's `grandTotalCredits` is authoritative (computed with
 *     BigInt-based `Decimal` per INV-SEC-02 — no floats).
 *   - Service deposit % comes from `product.serviceConfig.depositPercent`
 *     (snapshot of seller's config at product-create time — INV-MKT-14
 *     partial). The 50% default is from SPEC-081 Fase 5 §0 Q1 baseline.
 *   - The `Idempotency-Key` HTTP header is generated via `safeUUID()`
 *     (INV-SEC-03). Retries within the same modal session REUSE the same
 *     UUID (so a network hiccup doesn't double-debit); a new modal open
 *     generates a fresh UUID.
 *   - NO `as any` (P-041) — dynamic href on `router.push` cast with
 *     `as Route` per CM-51 (typedRoutes enabled in next.config.ts).
 *   - NO `console.*` (P-042) — errors surfaced via toast / inline error
 *     message.
 *
 * Reuse: `TwoFactorFinancialDialog` (TOTP — financial-grade op per
 * INV-SEC-06 §9.6). The dialog opens when the user clicks "Verificar 2FA"
 * in the summary step; on verify, the modal transitions to the verify
 * step. On 403 `2FA_REQUIRED` from the create call, the dialog re-opens
 * (the user's 5-min Redis key may have expired between summary and verify).
 *
 * Anti-P-043 (CM-28): the in-flight `api.market.orders.create()` call is
 * tracked via `submitting` state — disables the submit button + closes
 * the modal's escape hatch (the user can't close mid-submit).
 *
 * Anti-P-044 (ARIA): all interactive elements have explicit `aria-label`
 * or are wrapped in `<Label>`. Step indicators use `aria-current="step"`.
 *
 * Refactor note (Fase 5 Task C1): the original Fase 4 modal was physical-
 * only (1200 LoC single-component). The 3-variant refactor extracts the
 * shared helpers (resolveShipping, estimateOrder, formatCredits, MAX_QUANTITY,
 * mapCreateError) + adds 2 new variant components (ServiceCheckoutModal +
 * DigitalCheckoutModal) alongside the existing PhysicalCheckoutModal
 * (behavior preserved verbatim — only the wrapper `CheckoutModal` decides
 * which variant to render based on `product.type`).
 *
 * SPEC-244 · partición (SPEC244-IA18-FE-PAGES) — el monolito de 2.637 L
 * fue particionado VERBATIM en:
 *   - index.tsx                               — este archivo (router ≤200 L).
 *   - types.ts                                — `Step` + `CheckoutModalProps`.
 *   - constants.ts                            — `MAX_QUANTITY` +
 *                                               `SERVICE_DEPOSIT_PERCENT_FALLBACK`.
 *   - helpers.ts                              — `resolveShipping` +
 *                                               `resolvePickupInfo` +
 *                                               `estimateOrder` +
 *                                               `formatCredits` +
 *                                               `mapCreateError`.
 *   - hooks/use-physical-checkout.ts          — estado + handlers Physical.
 *   - hooks/use-service-checkout.ts           — estado + handlers Service.
 *   - hooks/use-digital-checkout.ts           — estado + handlers Digital.
 *   - sub-components/physical-checkout-modal.tsx — vista fina Physical.
 *   - sub-components/physical-details-step.tsx   — Physical Step 1.
 *   - sub-components/physical-summary-step.tsx   — Physical Step 2.
 *   - sub-components/physical-verify-step.tsx    — Physical Step 3.
 *   - sub-components/service-checkout-modal.tsx  — vista fina Service.
 *   - sub-components/service-details-step.tsx    — Service Step 1.
 *   - sub-components/service-summary-step.tsx    — Service Step 2.
 *   - sub-components/service-verify-step.tsx     — Service Step 3.
 *   - sub-components/digital-checkout-modal.tsx  — vista fina Digital.
 *   - sub-components/digital-summary-step.tsx    — Digital Step 1.
 *   - sub-components/digital-verify-step.tsx     — Digital Step 2.
 *   - sub-components/step-indicator.tsx          — shared 1·2·3 progress.
 *   - sub-components/submit-error-alert.tsx      — shared error alert.
 *   - sub-components/saved-address-selector.tsx  — Sprint 139 PR-H address
 *                                                  book radio list.
 *   - sub-components/two-fa-checkout-dialog.tsx  — shared 2FA dialog wrapper.
 *
 * API pública INMUTABLE: el export nombrado `CheckoutModal` + el tipo
 * `CheckoutModalProps` se re-exportan idénticos. Import path
 * `@/components/market/checkout-modal` resuelve a este `index.tsx`
 * automáticamente.
 *
 * Transformaciones (las ÚNICAS):
 *   - T-EXPORT: re-export de `CheckoutModal` + `CheckoutModalProps` desde
 *     el módulo de partición (`./types` + la invocación del router).
 *   - T-IMPORTS: el router solo importa `CheckoutModalProps` + los 3
 *     variantes desde `./sub-components/*` (cero imports de UI/React).
 *   - T-HOOK-EXTRACTION: cero — el router es tan delgado que no tiene
 *     estado propio (los 3 variantes son dueños de su propio hook).
 *   - T-PROPS-PASSING: el router pasa `props` (ya tipado
 *     `CheckoutModalProps`) spread al variante seleccionado.
 */

import type { CheckoutModalProps } from './types';
import { PhysicalCheckoutModal } from './sub-components/physical-checkout-modal';
import { ServiceCheckoutModal } from './sub-components/service-checkout-modal';
import { DigitalCheckoutModal } from './sub-components/digital-checkout-modal';

// Re-export the public props type so the existing import
// `import { CheckoutModal, CheckoutModalProps } from '@/components/market/checkout-modal'`
// keeps resolving VERBATIM.
export type { CheckoutModalProps } from './types';

// ─── Top-level router (decides which variant to render) ─────────────────────

export function CheckoutModal(props: CheckoutModalProps) {
  const { product } = props;

  // Dispatch by `product.type` (Fase 5 Task C1 — 3 variants per SPEC §6).
  // The router is intentionally a thin switch — each variant owns its
  // own state + Dialog + 2FA flow (no shared state across variants).
  if (product.type === 'service') {
    return <ServiceCheckoutModal {...props} />;
  }
  if (product.type === 'digital') {
    return <DigitalCheckoutModal {...props} />;
  }
  // physical (default — preserves Fase 4 behavior verbatim)
  return <PhysicalCheckoutModal {...props} />;
}
